import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  listPublishedReviews,
  computeRating,
  createReview,
  reportReview,
  markReviewHelpful,
  formatReviewDate,
  REPORT_REASONS,
  type CoreReview,
} from '@/lib/coreReviews';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 54. RATING / REVIEWS (affichage public)
// Section d'avis réutilisable, branchée sur la fiche publique /business/:slug
// et sur les fiches produit / service. Seuls les avis PUBLIÉS sont affichés.
// Un visiteur peut déposer un avis (mis en attente de modération), signaler
// un avis, et marquer un avis comme utile.
// ─────────────────────────────────────────────────────────────

interface ReviewsSectionProps {
  organizationId: number | null;
  entityType?: string;
  entityId?: string | number | null;
  /** Titre affiché dans l'en-tête de la section. */
  title?: string;
  /** Message quand aucun avis n'est publié. */
  emptyLabel?: string;
  /** Autorise le dépôt d'un avis par un visiteur. */
  allowSubmit?: boolean;
  /** Nom pré-rempli de l'auteur (facultatif). */
  currentUserName?: string | null;
  className?: string;
}

function Stars({ rating, size = 'text-sm' }: { rating: number; size?: string }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${rating.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={`${n <= full ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'} ${size}`}
        ></i>
      ))}
    </span>
  );
}

function InteractiveStars({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          className="cursor-pointer transition-transform hover:scale-110"
          aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
        >
          <i className={`${n <= shown ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'} text-2xl`}></i>
        </button>
      ))}
    </div>
  );
}

const INPUT =
  'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ReviewsSection({
  organizationId,
  entityType = 'business',
  entityId = null,
  title = 'Avis',
  emptyLabel = 'Aucun avis publié pour le moment.',
  allowSubmit = true,
  currentUserName = null,
  className = '',
}: ReviewsSectionProps) {
  const [reviews, setReviews] = useState<CoreReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [helpfulIds, setHelpfulIds] = useState<Set<number>>(new Set());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ authorName: '', rating: 5, title: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [reportTarget, setReportTarget] = useState<CoreReview | null>(null);
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0].key);
  const [reportDetail, setReportDetail] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reporting, setReporting] = useState(false);

  const load = useCallback(async () => {
    if (organizationId == null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rows = await listPublishedReviews(organizationId, { entityType, entityId });
      setReviews(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les avis.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, entityType, entityId]);

  useEffect(() => {
    setForm((f) => ({ ...f, authorName: currentUserName || f.authorName }));
  }, [currentUserName]);

  useEffect(() => {
    load();
  }, [load]);

  const dist = useMemo(() => computeRating(reviews), [reviews]);

  const handleHelpful = async (review: CoreReview) => {
    if (helpfulIds.has(review.id)) return;
    setHelpfulIds((prev) => new Set(prev).add(review.id));
    setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r)));
    try {
      await markReviewHelpful(review.id, review.helpful_count || 0);
    } catch {
      /* best-effort */
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (organizationId == null) return;
    const name = form.authorName.trim();
    if (!name) {
      setFormMsg({ type: 'err', text: 'Merci d’indiquer votre nom.' });
      return;
    }
    if (form.comment.trim().length < 10) {
      setFormMsg({ type: 'err', text: 'Votre commentaire doit contenir au moins 10 caractères.' });
      return;
    }
    setSubmitting(true);
    setFormMsg(null);
    try {
      const targetId = entityId != null && entityId !== '' ? entityId : (entityType === 'business' ? organizationId : null);
      await createReview({
        tenantId: null,
        organizationId,
        authorName: name,
        entityType,
        entityId: targetId,
        rating: form.rating,
        title: form.title.trim() || null,
        comment: form.comment.trim(),
        status: 'pending',
      });
      setFormMsg({ type: 'ok', text: 'Merci ! Votre avis sera publié après vérification par le professionnel.' });
      setForm({ authorName: name, rating: 5, title: '', comment: '' });
      setShowForm(false);
    } catch (err) {
      setFormMsg({ type: 'err', text: err instanceof Error ? err.message : 'Envoi impossible pour le moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (!reportTarget || organizationId == null) return;
    setReporting(true);
    try {
      await reportReview({
        reviewId: reportTarget.id,
        organizationId,
        reason: reportReason,
        detail: reportDetail.trim() || null,
      });
      setReportSent(true);
    } catch {
      setReportSent(true);
    } finally {
      setReporting(false);
    }
  };

  if (organizationId == null) return null;

  return (
    <section className={`bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 ${className}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-star-smile-line text-accent-500"></i>{title}
          {dist.count > 0 && <span className="text-sm font-normal text-foreground-400">({dist.count})</span>}
        </h2>
        {allowSubmit && (
          <button
            type="button"
            onClick={() => { setShowForm((v) => !v); setFormMsg(null); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={showForm ? 'ri-close-line' : 'ri-quill-pen-line'}></i>
            {showForm ? 'Annuler' : 'Laisser un avis'}
          </button>
        )}
      </div>

      {formMsg && formMsg.type === 'ok' && (
        <div className="mb-4 px-3 py-2.5 rounded-md bg-primary-50 border border-primary-200 text-xs text-primary-800 flex items-start gap-2">
          <i className="ri-checkbox-circle-line mt-0.5"></i>{formMsg.text}
        </div>
      )}

      {/* Formulaire de dépôt d'avis */}
      {allowSubmit && showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 rounded-lg bg-background-100/60 border border-background-200/60 space-y-3">
          <div>
            <span className="block text-xs font-medium text-foreground-600 mb-1.5">Votre note</span>
            <InteractiveStars value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Votre nom</span>
              <input className={INPUT} value={form.authorName} onChange={(e) => setForm({ ...form, authorName: e.target.value })} placeholder="Ex : Amina T." maxLength={80} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre (optionnel)</span>
              <input className={INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Résumez votre expérience" maxLength={120} />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">Votre commentaire</span>
            <textarea className={`${INPUT} min-h-[90px]`} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Partagez votre expérience…" maxLength={1000}></textarea>
          </label>
          {formMsg && formMsg.type === 'err' && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">{formMsg.text}</div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              {submitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>} Publier mon avis
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center py-8 text-center">
          <i className="ri-error-warning-line text-2xl text-red-400 mb-2"></i>
          <p className="text-sm text-foreground-600 mb-3">{error}</p>
          <button type="button" onClick={load} className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer">Réessayer</button>
        </div>
      ) : dist.count === 0 ? (
        <div className="flex flex-col items-center py-8 text-center">
          <span className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-chat-3-line text-xl text-foreground-400"></i>
          </span>
          <p className="text-sm text-foreground-500">{emptyLabel}</p>
        </div>
      ) : (
        <>
          {/* Résumé de note */}
          <div className="flex flex-col sm:flex-row gap-5 sm:gap-8 mb-6 pb-6 border-b border-background-200/70">
            <div className="flex flex-col items-center justify-center text-center sm:w-40 flex-shrink-0">
              <span className="text-4xl font-bold font-heading text-foreground-950">{dist.average.toFixed(1)}</span>
              <Stars rating={dist.average} size="text-base" />
              <span className="text-xs text-foreground-400 mt-1">{dist.count} avis publié{dist.count > 1 ? 's' : ''}</span>
            </div>
            <div className="flex-1 space-y-1.5">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = dist.byRating[n] || 0;
                const pct = dist.count > 0 ? Math.round((c / dist.count) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2">
                    <span className="w-10 text-xs text-foreground-500 tabular-nums">{n} <i className="ri-star-fill text-accent-500"></i></span>
                    <div className="flex-1 h-2 rounded-full bg-background-100 overflow-hidden">
                      <div className="h-full bg-accent-500" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="w-8 text-right text-xs text-foreground-400 tabular-nums">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Liste des avis */}
          <ul className="space-y-4">
            {reviews.map((r) => (
              <li key={r.id} className="pb-4 border-b border-background-100 last:border-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-8 h-8 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(r.author_name || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="text-sm font-semibold text-foreground-900">{r.author_name || 'Anonyme'}</span>
                      {r.is_verified && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-[10px] font-medium">
                          <i className="ri-verified-badge-line"></i>Achat vérifié
                        </span>
                      )}
                      {r.is_factual && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[10px] font-medium">
                          <i className="ri-information-line"></i>Donnée factuelle
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Stars rating={r.rating || 0} />
                      <span className="text-xs text-foreground-400">{formatReviewDate(r.created_at)}</span>
                    </div>
                  </div>
                </div>
                {r.title && <p className="text-sm font-semibold text-foreground-900 mt-2">{r.title}</p>}
                {r.comment && <p className="text-sm text-foreground-600 leading-relaxed mt-1 whitespace-pre-line">{r.comment}</p>}

                {r.reply_body && (
                  <div className="mt-3 ml-4 pl-3 border-l-2 border-primary-200 bg-background-100/50 rounded-r-md py-2 pr-3">
                    <p className="text-xs font-semibold text-primary-800 flex items-center gap-1.5 mb-1">
                      <i className="ri-reply-line"></i>Réponse du professionnel
                    </p>
                    <p className="text-xs text-foreground-600 leading-relaxed">{r.reply_body}</p>
                  </div>
                )}

                <div className="flex items-center gap-4 mt-3">
                  <button
                    type="button"
                    onClick={() => handleHelpful(r)}
                    disabled={helpfulIds.has(r.id)}
                    className={`inline-flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${helpfulIds.has(r.id) ? 'text-primary-600' : 'text-foreground-400 hover:text-foreground-700'}`}
                  >
                    <i className={helpfulIds.has(r.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                    Utile{r.helpful_count ? ` · ${r.helpful_count}` : ''}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setReportTarget(r); setReportReason(REPORT_REASONS[0].key); setReportDetail(''); setReportSent(false); }}
                    className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-flag-line"></i>Signaler
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-5 text-[11px] text-foreground-400 flex items-start gap-1.5 leading-relaxed">
        <i className="ri-information-line mt-0.5"></i>
        Les avis sont les opinions de leurs auteurs ; ils sont publiés après modération et sont distincts des informations factuelles.
      </p>

      {/* Modale de signalement */}
      {reportTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setReportTarget(null)}></div>
          <div className="relative bg-background-50 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            {!reportSent ? (
              <>
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-4">Signaler cet avis</h3>
                <div className="space-y-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Motif</span>
                    <select className={INPUT} value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                      {REPORT_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Détails (optionnel)</span>
                    <textarea className={`${INPUT} min-h-[80px]`} value={reportDetail} onChange={(e) => setReportDetail(e.target.value)} placeholder="Expliquez brièvement le problème…" maxLength={500}></textarea>
                  </label>
                </div>
                <div className="flex items-center justify-end gap-3 mt-5">
                  <button type="button" onClick={() => setReportTarget(null)} className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
                  <button type="button" onClick={handleReport} disabled={reporting} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                    {reporting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-flag-line"></i>} Signaler
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-2">
                <span className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-checkbox-circle-line text-2xl"></i>
                </span>
                <h3 className="text-base font-bold font-heading text-foreground-950 mb-1">Signalement envoyé</h3>
                <p className="text-sm text-foreground-500 mb-5">Merci, notre équipe va examiner cet avis.</p>
                <button type="button" onClick={() => setReportTarget(null)} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}